/**
 * Country the DDI selector should pre-select from locale/location signals.
 *
 * WHY THIS RETURNS A COUNTRY AND NOT A BOOLEAN. It used to answer "should we
 * auto-select?" and the caller hardcoded Spain, which worked only while Spain
 * was the single case. Adding Portugal to that shape means the caller decides
 * WHICH country from signals it does not own — so the rule and the country it
 * implies now travel together, and a third locale is one line here.
 *
 * Signals, in precedence order:
 *
 *   1. `location`, a geo hint written as an alpha-3 by the platform. It beats
 *      the locale because where the visitor IS predicts their phone number
 *      better than the language they read in — someone in Lisbon reading the
 *      site in English still has a +351 number.
 *   2. `locale`, the language they chose or inherited.
 *
 * Triggers only when there is no incoming ddi (URL/userData) AND no country
 * already selected internally: an explicit choice is never overwritten.
 *
 * @param {object} signals
 * @param {string} [signals.ddi]            calling code coming from URL/userData
 * @param {string} [signals.selectedAlpha3] country already chosen in the component
 * @param {string} [signals.locale]         BCP-47 tag from the shared cookie
 * @param {string} [signals.location]       alpha-3 geo hint
 * @returns {string|null} alpha-3 to select, or null to leave the field alone
 */
export function resolveAutoSelectAlpha3({
    ddi,
    selectedAlpha3,
    locale,
    location,
}) {
    if (ddi) return null;
    if (selectedAlpha3) return null;

    const byLocation = LOCATION_TO_ALPHA3[location];
    if (byLocation) return byLocation;

    return LOCALE_TO_ALPHA3[locale] || null;
}

/**
 * Geo hints this rule acts on. Only the countries the rule covers are listed —
 * a visitor in a country with no entry keeps the default ordering.
 */
const LOCATION_TO_ALPHA3 = {
    ESP: "ESP",
    PRT: "PRT",
};

/**
 * Locales that imply a country.
 *
 * pt-BR is deliberately ABSENT. Brazil is already first in the pinned list
 * (`PRIORITY_ALPHA3`) and is the default the form opens on; adding it here
 * would change nothing for Brazilian visitors and would make the rule look
 * like it covers every locale, which it does not.
 */
const LOCALE_TO_ALPHA3 = {
    "es-ES": "ESP",
    "pt-PT": "PRT",
};

/**
 * Whether the selector should auto-select at all.
 *
 * Thin wrapper over `resolveAutoSelectAlpha3`, kept because it reads better at
 * the call site as a guard.
 *
 * @param {object} signals same shape as `resolveAutoSelectAlpha3`
 * @returns {boolean}
 */
export function shouldAutoSelectByLocale(signals) {
    return resolveAutoSelectAlpha3(signals) !== null;
}
